import { useMemo } from 'react';

import { METRICS_COLUMNS } from '@/Pages/DatasetSessionCompare/constants';
import { useExamples, useSessions } from '@/hooks/useSwr';
import { DatasetSchema, RunFacetField } from '@/types/schema';

import {
  CORE_COLUMNS,
  getColumnDisplayName,
} from '../../utils/columnTypeUtils';
import { HIDDEN_BY_DEFAULT_METADATA_KEYS } from '../ExperimentsGroupByTable/utils';

const DEFAULT_METRICS = Object.keys(METRICS_COLUMNS);

export const useDatasetColumnSuggestionOptions = (
  dataset: DatasetSchema,
  facets?: RunFacetField[],
  noAttachments?: boolean
) => {
  const { data: examplesData } = useExamples(
    dataset?.id ? { dataset: dataset.id, limit: 1 } : null
  );

  const { data: sessionsData } = useSessions(
    dataset?.id
      ? { reference_dataset: [dataset.id], limit: 10, include_stats: true }
      : null,
    undefined,
    { revalidateOnFocus: false }
  );

  return useMemo(() => {
    const suggestions = new Set<string>();

    // Add core columns
    const coreColumns = CORE_COLUMNS.filter(
      (col) => !noAttachments || col !== 'attachments'
    );
    coreColumns.forEach((col) => suggestions.add(col));

    // Add standard metrics
    DEFAULT_METRICS.forEach((col) =>
      suggestions.add(getColumnDisplayName(`metrics.${col}`))
    );

    // Extract keys from dataset schema if available
    if (dataset.inputs_schema_definition?.properties) {
      Object.keys(dataset.inputs_schema_definition.properties).forEach(
        (key) => {
          suggestions.add(getColumnDisplayName(`inputs.${key}`));
        }
      );
    }

    if (dataset.outputs_schema_definition?.properties) {
      Object.keys(dataset.outputs_schema_definition.properties).forEach(
        (key) => {
          suggestions.add(getColumnDisplayName(`outputs.${key}`));
          suggestions.add(getColumnDisplayName(`reference_outputs.${key}`));
        }
      );
    }

    // Extract keys from actual example data as fallback
    const firstExample = examplesData?.[0];
    if (firstExample) {
      if (firstExample.inputs) {
        Object.keys(firstExample.inputs).forEach((key) => {
          suggestions.add(getColumnDisplayName(`inputs.${key}`));
        });
      }

      if (firstExample.outputs) {
        Object.keys(firstExample.outputs).forEach((key) => {
          suggestions.add(getColumnDisplayName(`outputs.${key}`));
          suggestions.add(getColumnDisplayName(`reference_outputs.${key}`));
        });
      }
    }

    // Extract feedback keys from sessions
    if (sessionsData?.rows) {
      sessionsData.rows.forEach((session) => {
        if (session.feedback_stats) {
          Object.keys(session.feedback_stats).forEach((key) => {
            suggestions.add(getColumnDisplayName(`feedback.${key}`));
          });
        }
      });
    }

    // Extract metadata keys
    if (facets) {
      // First filter all metadata keys
      const allMetadataKeys = facets.filter(
        (facet) => facet.key === 'metadata_key'
      );

      const filteredKeys = allMetadataKeys.filter(
        (key) => !HIDDEN_BY_DEFAULT_METADATA_KEYS.includes(key.value)
      );

      filteredKeys.forEach((key) => {
        suggestions.add(getColumnDisplayName(`metadata.${key.value}`));
      });
    }

    return Array.from(suggestions).sort();
  }, [dataset, examplesData, sessionsData, facets]);
};
